// src/pages/locations/hooks/useCountriesList.ts
import React from "react";

import { useCountries, useDeleteCountryMutation } from "@/api/features/locations";
import type { LocationCountry } from "@/api/features/locations/types";

import { useDeleteConfirm } from "./useDeleteConfirm";

const PAGE_SIZE = 10;

export type CountriesFilters = {
  iso2?: string;
  name?: string;
};

function normIso2(v: unknown): string {
  return String(v ?? "").trim().toUpperCase();
}

/** List + delete state for the Countries tab. Create/edit live on their own pages. */
export function useCountriesList(params: { search: string }) {
  const search = params.search;
  const [filters, setFilters] = React.useState<CountriesFilters>({});
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [search, filters.iso2, filters.name]);

  const {
    data: listRes,
    isLoading,
    isFetching,
  } = useCountries({
    page,
    limit: PAGE_SIZE,
    q: search.trim() || undefined,
    iso2: filters.iso2?.trim() || undefined,
    name: filters.name?.trim() || undefined,
  });

  const rows = React.useMemo(() => listRes?.items ?? [], [listRes]);
  const total = listRes?.total ?? 0;

  const deleteMutation = useDeleteCountryMutation();
  const deleting = deleteMutation.isPending;

  const remove = React.useCallback(
    (row: LocationCountry, done: () => void) => {
      deleteMutation.mutate(
        { iso2: normIso2(row.iso2) },
        { onSettled: done }
      );
    },
    [deleteMutation]
  );

  const { confirmDelete, askDelete, cancelDelete, confirmDeleteRow } =
    useDeleteConfirm<LocationCountry>({ isDeleting: deleting, remove });

  return {
    filters,
    setFilters,

    page,
    setPage,
    pageSize: PAGE_SIZE,

    rows,
    total,
    isLoading,
    isFetching,

    deleting,
    confirmDelete,
    askDelete,
    cancelDelete,
    confirmDeleteRow,
  };
}
